import { Button, MenuButton, Tooltip } from "@narmi/design_system";
import { useEffect, useState } from "react";
import { useNotificationContext } from "cerulean";
import { useLocalization } from "@fluent/react";
import { DisplayOnceTokenDialog, TermsOfUseDialog } from "./modals";
import { deserializeBankingToken, listBankingTokens } from "./api";
import {
  TokenCreateDialog,
  TokenRevokeDialog,
  TokenUpdateDialog,
} from "./tokenWriteFormModals";
import {
  BankingTokenRecord,
  CreatedBankingTokenRecord,
  DeserializedBankingToken,
  TableColumn,
} from "./types";
import { useCurrentUser } from "../../contexts/CurrentUserContext";

const BusinessBankingAPITokenList = () => {
  const { currentUser } = useCurrentUser();
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const [tokenList, setTokenList] = useState<DeserializedBankingToken[]>([]);

  const [tosModalOpen, setTosModalOpen] = useState(false);
  const [tokenCreateModalOpen, setTokenCreateModalOpen] = useState(false);

  const [tokenForUpdate, setTokenForUpdate] =
    useState<DeserializedBankingToken | null>(null);
  const [tokenForRevoke, setTokenForRevoke] =
    useState<DeserializedBankingToken | null>(null);
  const [createdToken, setCreatedToken] =
    useState<CreatedBankingTokenRecord | null>(null);

  const tableColumns: TableColumn[] = [
    { header: l10n.getString("table-header-nickname"), fieldName: "nickname" },
    {
      header: l10n.getString("table-header-created-by"),
      fieldName: "createdBy",
    },
    { header: l10n.getString("table-header-created"), fieldName: "created" },
    {
      header: l10n.getString("table-header-expires"),
      fieldName: "expires",
      cellFloatRightCallback: (row: DeserializedBankingToken) =>
        row.isExpired ? (
          <Tooltip text={l10n.getString("tooltip-token-expired")}>
            <span
              className="narmi-icon-info"
              style={{ color: "var(--color-errorDark)" }}
            ></span>
          </Tooltip>
        ) : null,
    },
    { header: l10n.getString("table-header-last-used"), fieldName: "lastUsed" },
    {
      header: l10n.getString("table-header-scopes"),
      fieldName: "scopesDisplay",
    },
    {
      header: l10n.getString("table-header-allowlisted-ips"),
      fieldName: "ipAllowListDisplay",
    },
  ];

  useEffect(() => {
    const fetchTokens = async () => {
      const response = await listBankingTokens();
      setTokenList(response.results.map(deserializeBankingToken));
    };

    fetchTokens();
  }, []);

  const tosModalOnConfirm = () => {
    setTosModalOpen(false);
    setTokenCreateModalOpen(true);
  };

  const canWriteToken = (token: DeserializedBankingToken) =>
    Boolean(
      currentUser &&
        (currentUser.org_role === "Admin" ||
          token.createdBy === currentUser.username) &&
        !token.isExpired
    );

  const tokenCreateModalOnConfirm = (
    createdTokenResponse: CreatedBankingTokenRecord
  ) => {
    setTokenList([...tokenList, deserializeBankingToken(createdTokenResponse)]);
    setTokenCreateModalOpen(false);
    setCreatedToken(createdTokenResponse);
    sendNotification({
      type: "dismissible_success",
      text: l10n.getString("notification-token-created"),
    });
  };

  const setTokenState = (tokenState: BankingTokenRecord) => {
    const tokenListState = tokenList.map((token) =>
      token.uuid === tokenState.uuid
        ? deserializeBankingToken(tokenState)
        : token
    );

    setTokenList(tokenListState);
  };

  const tokenUpdateModalOnConfirm = (
    updatedTokenResponse: BankingTokenRecord
  ) => {
    setTokenState(updatedTokenResponse);
    setTokenForUpdate(null);
    sendNotification({
      type: "dismissible_success",
      text: l10n.getString("notification-token-updated"),
    });
  };

  const tokenDeleteModalOnConfirm = (
    revokedTokenResponse: BankingTokenRecord
  ) => {
    setTokenState(revokedTokenResponse);
    setTokenForRevoke(null);
    sendNotification({
      type: "dismissible_success",
      text: l10n.getString("notification-token-revoked"),
    });
  };

  return (
    <>
      {/* Begin Modals */}

      <TermsOfUseDialog
        isOpen={tosModalOpen}
        onUserDismiss={() => setTosModalOpen(false)}
        onConfirm={tosModalOnConfirm}
      />
      <TokenCreateDialog
        isOpen={tokenCreateModalOpen}
        onUserDismiss={() => setTokenCreateModalOpen(false)}
        onConfirm={tokenCreateModalOnConfirm}
      />
      <DisplayOnceTokenDialog
        isOpen={Boolean(createdToken)}
        onUserDismiss={() => setCreatedToken(null)}
        createdToken={createdToken}
      />
      <TokenUpdateDialog
        isOpen={Boolean(tokenForUpdate)}
        onUserDismiss={() => setTokenForUpdate(null)}
        onConfirm={tokenUpdateModalOnConfirm}
        tokenForUpdate={tokenForUpdate}
      />
      <TokenRevokeDialog
        isOpen={Boolean(tokenForRevoke)}
        onUserDismiss={() => setTokenForRevoke(null)}
        onConfirm={tokenDeleteModalOnConfirm}
        tokenForRevoke={tokenForRevoke}
      />

      {/* End Modals */}

      <div
        className="padding--top--l"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "40px",
        }}
      >
        <div>
          {l10n.getString("text-for-more-info")}&nbsp;
          <a
            target="_blank"
            href="/v1/banking/docs/"
          >
            {l10n.getString("link-text-api-documentation")}
          </a>
        </div>

        <Button
          onClick={() => setTosModalOpen(true)}
          label={l10n.getString("button-new-api-token")}
        ></Button>
      </div>
      {/* Begin Data Table */}
      <div className="padding--top--xl" style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <thead
            className="fontSize--xs fontWeight--bold"
            style={{ textTransform: "uppercase" }}
          >
            <tr>
              {tableColumns.map((col) => (
                <th
                  key={col.fieldName}
                  style={{
                    textAlign: "left",
                    padding: "8px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {col.header}
                </th>
              ))}
              <th style={{ width: "40px" }}></th>
            </tr>
          </thead>
          <tbody>
            {tokenList.map((row, idx) => (
              <tr key={idx}>
                {tableColumns.map((col) => (
                  <td
                    key={col.fieldName}
                    style={{
                      width: `${100 / (tableColumns.length + 1)}%`,
                      padding: "8px",
                      backgroundColor: "white",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      {row[col.fieldName as keyof typeof row]}
                      {col.cellFloatRightCallback &&
                        col.cellFloatRightCallback(row)}
                      &nbsp;
                    </div>
                  </td>
                ))}
                <td
                  style={{
                    width: "40px",
                    backgroundColor: "white",
                  }}
                >
                  <div
                    style={{
                      opacity: canWriteToken(row) ? 1 : 0.5,
                      pointerEvents: canWriteToken(row) ? "auto" : "none",
                    }}
                  >
                    <MenuButton>
                      <MenuButton.Item
                        label={l10n.getString("menu-item-edit")}
                        onSelect={() => setTokenForUpdate(row)}
                        startIcon="edit-2"
                      />
                      <MenuButton.Item
                        label={l10n.getString("menu-item-delete")}
                        onSelect={() => setTokenForRevoke(row)}
                        startIcon="trash"
                      />
                    </MenuButton>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* End Data Table */}
    </>
  );
};

export default BusinessBankingAPITokenList;
